import React, { useState, useEffect } from "react";
import { Col, Card, CardBody, Form, Input, Button } from "reactstrap";
import { Link } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDispatch, useSelector } from "react-redux";
import { fetchIssuesRequest, searchPaymentIssueRequest } from "../../store/PaymentIssues/paymentIssuesAction";
import PageSection from '../../components/Loader/loader';

const PaymentIssuesTable = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(30);
  const [displayedItems, setDisplayedItems] = useState([]);
  const [fetchedEntries, setFetchedEntries] = useState([]);
  const [totalFetchedCount, setTotalFetchedCount] = useState(0);
  const [noResultsFound, setNoResultsFound] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  const dispatch = useDispatch();
  const { paymentIssuesData, searchResults = [], loading } = useSelector(
    (state) => state.paymentIssues
  );

  useEffect(() => {
    if (!paymentIssuesData || paymentIssuesData.length === 0) {
      dispatch(fetchIssuesRequest(1, 120));
    } else {
      setDisplayedItems(paymentIssuesData);
    }
  }, [dispatch, paymentIssuesData]);

  useEffect(() => {
    const handleSearch = () => {
      if (searchTerm.length >= 3) {
        setSearchLoading(true);
        dispatch(searchPaymentIssueRequest(searchTerm));
      } else if (searchTerm.length === 0) {
        setDisplayedItems(paymentIssuesData);
        setNoResultsFound(false);
        setSearchLoading(false);
      }
    };

    const timeoutId = setTimeout(handleSearch, 500);

    return () => {
      clearTimeout(timeoutId);
      setSearchLoading(false);
    };
  }, [searchTerm, dispatch, paymentIssuesData]);

  useEffect(() => {
    if (searchTerm.length >= 3) {
      setSearchLoading(false);
      if (searchResults.length > 0) {
        setDisplayedItems(searchResults);
        setNoResultsFound(false);
      } else {
        setNoResultsFound(true);
        const clearItemsTimeout = setTimeout(() => {
          setDisplayedItems([]);
        }, 500);

        return () => clearTimeout(clearItemsTimeout);
      }
    } else {
      setDisplayedItems(paymentIssuesData);
      setNoResultsFound(false);
    }

  }, [searchResults, searchTerm, paymentIssuesData]);

  useEffect(() => {
    if (currentPage % 4 === 0 && paymentIssuesData <= currentPage * itemsPerPage) {
      const nextPage = currentPage / 4 + 1;
      dispatch(fetchIssuesRequest(nextPage, 120));
    }
  }, [currentPage, dispatch, paymentIssuesData]);

  const totalPages = Math.ceil(displayedItems.length / itemsPerPage);
  const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
  const displayedPaginationItems = displayedItems.slice(indexOfFirstItem, indexOfFirstItem + itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const renderPageNumbers = () => {
    const pageNumbers = [];
    let startPage, endPage;

    if (totalPages <= 3) {
      startPage = 1;
      endPage = totalPages;
    } else {
      if (currentPage <= 2) {
        startPage = 1;
        endPage = 3;
      } else if (currentPage + 1 >= totalPages) {
        startPage = totalPages - 2;
        endPage = totalPages;
      } else {
        startPage = currentPage - 1;
        endPage = currentPage + 1;
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <li key={i} className={`page-item ${i === currentPage ? "active" : ""}`}>
          <Link to="#" className="page-link" onClick={() => paginate(i)}>
            {i}
          </Link>
        </li>
      );
    }

    return pageNumbers;
  };

  const handleDownloadCSV = () => {
    const headers = ["ID", "Device Name", "Device ID", "Description"];
    const csvData = displayedPaginationItems.map((issue) => [
      issue.id,
      issue.device_Name,
      issue.device_ID,
      issue.description,
    ]);
    const csvContent = [headers, ...csvData]
      .map((row) => row.join(","))
      .join("\n");
    const encodedUri = encodeURI("data:text/csv;charset=utf-8," + csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "payment_issues.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <React.Fragment>
      <ToastContainer />
      <Col lg={12}>
        <Card style={{ paddingBottom: "55px" }}>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-1">
              <div className="d-flex justify-content-between align-items-center">
                <h4 className="card-title mt-1">Payment Issues</h4>
              </div>
              <div className="d-flex align-items-center">
                <Form className="app-search d-none d-lg-block pt-2">
                  <div className="position-relative d-flex align-items-center">
                    <Input
                      type="text"
                      className="form-control"
                      style={{ width: "65%" }}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search"
                    />
                    <span className="ri-search-line"></span>
                    <Button
                      style={{ fontSize: "13px", padding: "7px 9px" }}
                      color="primary"
                      className="ms-2"
                      onClick={handleDownloadCSV}
                    >
                      Download CSV
                    </Button>
                  </div>
                </Form>
              </div>
            </div>
            {loading || searchLoading ? (
              <div className="table-responsive p-2" style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "50%"
              }}>
                <PageSection />
              </div>
            ) : (
              <div className="table-responsive">
                <table
                  className="table table-bordered"
                  style={{ minWidth: "max-content" }}
                >
                  <thead>
                    <tr>
                      <th className="p-3">No.</th>
                      <th className="p-3">Device Name</th>
                      <th className="p-3">Device ID</th>
                      <th className="p-3">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedPaginationItems.length === 0 ? (
                      <tr>
                        <td colSpan="12" className="text-center p-5">
                          {noResultsFound ? (
                            <div>No data found</div>
                          ) : (
                            <PageSection />
                          )}
                        </td>
                      </tr>
                    ) : (
                      displayedPaginationItems.map((issue, index) => (
                        <tr key={issue.id}>
                          <td className="p-3">{indexOfFirstItem + index + 1}</td>
                          <td className="p-3">{issue.device_Name}</td>
                          <td className="p-3">{issue.device_ID}</td>
                          <td className="p-3" style={{ maxWidth: "250px" }}>
                            {issue.description}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                  <nav>
                    <ul className="pagination justify-content-end">
                      <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                        <Link to="#" className="page-link" onClick={() => paginate(currentPage - 1)}>&laquo;</Link>
                      </li>
                      {renderPageNumbers()}
                      <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                        <Link to="#" className="page-link" onClick={() => paginate(currentPage + 1)}>&raquo;</Link>
                      </li>
                    </ul>
                  </nav>
              </div>
            )}
          </CardBody>
        </Card>
      </Col>
    </React.Fragment>
  );
};

export default PaymentIssuesTable;
